import { type JSX, useId } from "react"
import { styled } from "styled-system/jsx"

const Root = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xs",
  },
})

const Label = styled("span", {
  base: {
    textStyle: "heading",
    userSelect: "none",
  },
})

const ItemGroup = styled("div", {
  base: {
    display: "flex",
    alignItems: "center",
    gap: "md",
  },
})

const Item = styled("label", {
  base: {
    display: "flex",
    alignItems: "center",
    gap: "xs",
    cursor: "pointer",
    textStyle: "input",
    userSelect: "none",
    "&:has(:disabled)": {
      cursor: "not-allowed",
      opacity: "muted",
    },
  },
})

const Radio = styled("input", {
  base: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "[16px]",
    height: "[16px]",
    borderRadius: "full",
    border: true,
    backgroundColor: "surface",
    cursor: "pointer",
    transitionProperty: "[border-color]",
    transitionDuration: "[150ms]",
    _before: {
      content: '""',
      display: "block",
      width: "[8px]",
      height: "[8px]",
      borderRadius: "full",
      backgroundColor: "primary",
      transform: "scale(0)",
      transitionProperty: "[transform]",
      transitionDuration: "[150ms]",
    },
    _checked: {
      borderColor: "primary",
      _before: {
        transform: "scale(1)",
      },
    },
    _disabled: {
      cursor: "not-allowed",
    },
    _focusVisible: {
      outline: "[2px solid]",
      outlineColor: "primary",
      outlineOffset: "[2px]",
    },
  },
})

type RadioGroupProps<T extends string> = {
  items: { value: T; label: string }[]
  label?: string
  value: T
  // `NoInfer` keeps `T` pinned to `items`/`value`: a `useState` setter passed
  // here would otherwise widen it to the `string` constraint.
  onValueChange(value: NoInfer<T>): void
  disabled?: boolean
}

export const RadioGroup = <T extends string>({
  items,
  label,
  value,
  onValueChange,
  disabled,
}: RadioGroupProps<T>): JSX.Element => {
  // Shared `name` is what groups native radios for keyboard navigation.
  const name = useId()
  const labelId = useId()

  return (
    <Root
      role="radiogroup"
      aria-labelledby={label ? labelId : undefined}
    >
      {label ? <Label id={labelId}>{label}</Label> : undefined}
      <ItemGroup>
        {items.map((item) => (
          <Item key={item.value}>
            <Radio
              type="radio"
              name={name}
              value={item.value}
              checked={item.value === value}
              disabled={disabled}
              onChange={() => {
                onValueChange(item.value)
              }}
            />
            {item.label}
          </Item>
        ))}
      </ItemGroup>
    </Root>
  )
}
